import type { ReactNode } from 'react'

import Link from 'next/link'
import { Mail } from 'lucide-react'
import { formatNumber } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Breadcrumbs, PageBackdrop } from '@/components/courses/page-hero'

export type LegalSection = {
    id: string
    title: string
    paragraphs?: Array<string>
    bullets?: Array<string>
}

interface LegalPageProps {
    title: string
    description: string
    updated: string
    intro: string
    sections: Array<LegalSection>
}

const LegalPage = ({ title, description, updated, intro, sections }: LegalPageProps): ReactNode => {
    return (
        <Layout>
            <Seo title={title} description={description} />

            <section className='relative isolate pt-28 pb-8 sm:pt-32'>
                <PageBackdrop />

                <div className='mx-auto max-w-5xl px-5 sm:px-6 lg:px-8'>
                    <Breadcrumbs items={[{ label: "خانه", href: '/' }, { label: title }]} />
                    <h1 className='mt-5 text-3xl font-extrabold text-foreground sm:text-4xl'>
                        {title}
                    </h1>
                    <p className='mt-3 text-sm text-muted-foreground'>
                        {'آخرین به‌روزرسانی: '}
                        <span>{updated}</span>
                    </p>
                </div>
            </section>

            <section className='pb-6'>
                <div className='mx-auto grid max-w-5xl gap-8 px-5 sm:px-6 lg:grid-cols-[14rem_1fr] lg:px-8'>
                    <nav aria-label="فهرست مطالب" className='hidden lg:block'>
                        <div className='sticky top-28 rounded-3xl bg-white/80 p-4 shadow-soft ring-1 ring-white backdrop-blur'>
                            <p className='mb-2 px-2 text-xs font-bold text-muted-foreground'>در این صفحه</p>
                            <ol className='flex flex-col gap-0.5'>
                                {sections.map((section, index) => (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            className='flex gap-2 rounded-xl px-2 py-1.5 text-sm text-foreground/75 transition-colors hover:bg-primary-soft hover:text-primary'
                                        >
                                            <span className='text-muted-foreground'>{formatNumber(index + 1)}.</span>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </nav>

                    <article className='rounded-3xl bg-white p-6 shadow-soft ring-1 ring-black/[0.03] sm:p-10'>
                        <p className='text-base text-foreground/85'>
                            {intro}
                        </p>

                        {sections.map((section, index) => (
                            <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`} className='mt-8 scroll-mt-28 border-t border-border pt-8'>
                                <h2 id={`${section.id}-title`} className='text-xl font-bold text-foreground'>
                                    <span className='text-primary'>{formatNumber(index + 1)}.</span>
                                    {' '}
                                    {section.title}
                                </h2>

                                {section.paragraphs?.map((paragraph) => (
                                    <p key={paragraph} className='mt-3 text-base text-foreground/80'>
                                        {paragraph}
                                    </p>
                                ))}

                                {section.bullets && (
                                    <ul className='mt-3 flex flex-col gap-2'>
                                        {section.bullets.map((bullet) => (
                                            <li key={bullet} className='flex items-start gap-3 text-base text-foreground/80'>
                                                <span className='mt-3 size-1.5 shrink-0 rounded-full bg-primary' aria-hidden='true' />
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        ))}

                        <div className='mt-10 flex flex-col gap-3 rounded-2xl bg-secondary p-5 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='text-sm text-foreground/80'>
                                پرسشی درباره این صفحه دارید؟ از فرم تماس برایمان بنویسید.
                            </p>
                            <Link
                                href='/contact'
                                className='inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover'
                            >
                                <Mail className='size-4' aria-hidden='true' />
                                تماس با ما
                            </Link>
                        </div>
                    </article>
                </div>
            </section>
        </Layout>
    )
}

export default LegalPage
